/**
 * TimerAudioManager — Web Audio sounds for the timer pages.
 *
 * iOS audio session strategy: see PresenterAudioManager for full explanation.
 * Short version: a looping silent <audio> element locks the AVAudioSession into
 * "playback" category so sounds play regardless of the hardware mute switch.
 */

import { soundUrl, loadArrayBuffer } from '../../lib/platform';

let _silenceDataUrl = null;
function getSilenceDataUrl() {
  if (_silenceDataUrl) return _silenceDataUrl;
  const rate = 8000;
  const samples = rate / 10; // 100 ms = 800 samples
  const buf = new ArrayBuffer(44 + samples);
  const v = new DataView(buf);
  const str = (off, s) => [...s].forEach((c, i) => v.setUint8(off + i, c.charCodeAt(0)));
  str(0, 'RIFF');
  v.setUint32(4, 36 + samples, true);
  str(8, 'WAVE');
  str(12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);     // PCM
  v.setUint16(22, 1, true);     // mono
  v.setUint32(24, rate, true);
  v.setUint32(28, rate, true);  // byte rate
  v.setUint16(32, 1, true);     // block align
  v.setUint16(34, 8, true);     // 8-bit
  str(36, 'data');
  v.setUint32(40, samples, true);
  new Uint8Array(buf, 44).fill(128); // 128 = silence (unsigned 8-bit PCM)
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  _silenceDataUrl = 'data:audio/wav;base64,' + btoa(bin);
  return _silenceDataUrl;
}

export class TimerAudioManager {
  constructor() {
    this._ctx = null;
    this._sessionKeepAlive = null;
    this._keepAliveNode = null;
    this._buzzerBuffer = null;
    this._preloadBuzzerAttempted = false;
    this._boxCounterBuffer = null;
    this._preloadBoxCounterAttempted = false;
    this._startExerciseBuffer = null;
    this._preloadStartExerciseAttempted = false;
    this._breakBuffer = null;
    this._preloadBreakAttempted = false;
    this._bellStartBuffer = null;
    this._preloadBellStartAttempted = false;

    // Re-resume AudioContext and <audio> keep-alive when the user returns to
    // the app (screen unlock, app switcher, notification tap).
    this._onVisibilityChange = () => {
      if (!document.hidden) {
        if (this._ctx?.state === 'suspended') {
          this._ctx.resume().catch(() => {});
        }
        if (this._sessionKeepAlive?.paused) {
          this._sessionKeepAlive.play().catch(() => {});
        }
      }
    };
    this._onTouchStart = () => {
      if (this._ctx?.state === 'suspended') {
        this._ctx.resume().catch(() => {});
      }
      if (this._sessionKeepAlive?.paused) {
        this._sessionKeepAlive.play().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange);
    document.addEventListener('touchstart', this._onTouchStart, { passive: true });
  }

  _getCtx() {
    if (!this._ctx) {
      this._ctx = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this._ctx;
  }

  /**
   * Must be called from a user gesture to unlock AudioContext on iOS.
   * Returns a Promise — await it so the context is in "running" state
   * before the first timer tick fires.
   */
  async unlock() {
    // Looping silent <audio> — locks AVAudioSession into "playback" category.
    try {
      if (!this._sessionKeepAlive) {
        this._sessionKeepAlive = new Audio(getSilenceDataUrl());
        this._sessionKeepAlive.loop = true;
      }
      await this._sessionKeepAlive.play();
    } catch (e) {
      // ignore
    }

    // Resume Web Audio API context.
    try {
      const ctx = this._getCtx();
      if (ctx.state !== 'running') {
        await ctx.resume();
      }
      // 4096-sample silent buffer confirms unlock on iOS.
      const silent = ctx.createBuffer(1, 4096, ctx.sampleRate);
      const src = ctx.createBufferSource();
      src.buffer = silent;
      src.connect(ctx.destination);
      src.start(0);
    } catch (e) {
      // ignore
    }

    // Preload all MP3 sounds (Kampfsport, Pyramide, …).
    this._preloadBuzzer();
    this._preloadBoxCounter();
    this._preloadStartExercise();
    this._preloadBreak();
    this._preloadBellStart();

    // Secondary Web Audio keep-alive.
    if (!this._keepAliveNode) {
      try {
        const ctx = this._getCtx();
        const buf = ctx.createBuffer(1, 4096, ctx.sampleRate);
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.loop = true;
        src.connect(ctx.destination);
        src.start(0);
        this._keepAliveNode = src;
      } catch (e) {
        // ignore
      }
    }
  }

  // 880 Hz sine wave, 120ms — countdown beep for last 10 seconds
  playCountdownBeep() {
    try {
      const ctx = this._getCtx();
      if (ctx.state !== 'running') {
        ctx.resume().catch(() => {}); // best-effort recovery; this tick is skipped
        return;
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      gain.gain.setValueAtTime(0.35, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.12);
    } catch (e) {}
  }

  // Two ascending tones: 660 Hz then 880 Hz — end signal, clearly distinct from countdown beep
  playEndSignal() {
    try {
      const ctx = this._getCtx();
      if (ctx.state !== 'running') {
        ctx.resume().catch(() => {});
        return;
      }
      const now = ctx.currentTime;
      [[660, 0], [880, 0.2]].forEach(([freq, offset]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now + offset);
        gain.gain.setValueAtTime(0.5, now + offset);
        gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.35);
        osc.start(now + offset);
        osc.stop(now + offset + 0.4);
      });
    } catch (e) {}
  }

  _preloadBuzzer() {
    if (this._preloadBuzzerAttempted) return;
    this._preloadBuzzerAttempted = true;
    try {
      const ctx = this._getCtx();
      loadArrayBuffer(soundUrl('buzzer'))
        .then((buf) => ctx.decodeAudioData(buf))
        .then((decoded) => { this._buzzerBuffer = decoded; })
        .catch(() => {});
    } catch (e) {}
  }

  _preloadBoxCounter() {
    if (this._preloadBoxCounterAttempted) return;
    this._preloadBoxCounterAttempted = true;
    try {
      const ctx = this._getCtx();
      loadArrayBuffer(soundUrl('boxcounter'))
        .then((buf) => ctx.decodeAudioData(buf))
        .then((decoded) => { this._boxCounterBuffer = decoded; })
        .catch(() => {});
    } catch (e) {}
  }

  /** Play the box counter sound once when 10s remain in a fight round. */
  playBoxCounter() {
    try {
      const ctx = this._getCtx();
      if (ctx.state !== 'running') {
        ctx.resume().catch(() => {});
        return;
      }
      if (this._boxCounterBuffer) {
        const src = ctx.createBufferSource();
        src.buffer = this._boxCounterBuffer;
        src.connect(ctx.destination);
        src.start(0);
        return;
      }
      // Fallback: three short thuds
      const now = ctx.currentTime;
      [0, 0.25, 0.5].forEach((offset) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(180, now + offset);
        gain.gain.setValueAtTime(0.4, now + offset);
        gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.15);
        osc.start(now + offset);
        osc.stop(now + offset + 0.18);
      });
    } catch (e) {}
  }

  /** Play the buzzer sound at the end of a fight round. Falls back to a synthesized horn. */
  playBuzzer() {
    try {
      const ctx = this._getCtx();
      if (ctx.state !== 'running') {
        ctx.resume().catch(() => {});
        return;
      }
      if (this._buzzerBuffer) {
        const src = ctx.createBufferSource();
        src.buffer = this._buzzerBuffer;
        src.connect(ctx.destination);
        src.start(0);
        return;
      }
      // Fallback: synthesized horn-like sound (descending square wave)
      const now = ctx.currentTime;
      [[220, 0], [180, 0.25], [160, 0.5]].forEach(([freq, offset]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'square';
        osc.frequency.setValueAtTime(freq, now + offset);
        gain.gain.setValueAtTime(0.25, now + offset);
        gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.22);
        osc.start(now + offset);
        osc.stop(now + offset + 0.25);
      });
    } catch (e) {}
  }

  _preloadStartExercise() {
    if (this._preloadStartExerciseAttempted) return;
    this._preloadStartExerciseAttempted = true;
    try {
      const ctx = this._getCtx();
      loadArrayBuffer(soundUrl('start_exercise'))
        .then((buf) => ctx.decodeAudioData(buf))
        .then((decoded) => { this._startExerciseBuffer = decoded; })
        .catch(() => {});
    } catch (e) {}
  }

  /** Spielt start_exercise.mp3 beim Beginn jeder Arbeitsstufe. */
  playStartExercise() {
    try {
      const ctx = this._getCtx();
      if (ctx.state !== 'running') { ctx.resume().catch(() => {}); return; }
      if (this._startExerciseBuffer) {
        const src = ctx.createBufferSource();
        src.buffer = this._startExerciseBuffer;
        src.connect(ctx.destination);
        src.start(0);
        return;
      }
      // Fallback: zwei kurze aufsteigende Töne
      const now = ctx.currentTime;
      [[660, 0], [880, 0.12]].forEach(([freq, offset]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now + offset);
        gain.gain.setValueAtTime(0.4, now + offset);
        gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.1);
        osc.start(now + offset);
        osc.stop(now + offset + 0.12);
      });
    } catch (e) {}
  }

  _preloadBreak() {
    if (this._preloadBreakAttempted) return;
    this._preloadBreakAttempted = true;
    try {
      const ctx = this._getCtx();
      loadArrayBuffer(soundUrl('break'))
        .then((buf) => ctx.decodeAudioData(buf))
        .then((decoded) => { this._breakBuffer = decoded; })
        .catch(() => {});
    } catch (e) {}
  }

  /** Spielt break.mp3 beim Beginn jeder Pause. */
  playBreak() {
    try {
      const ctx = this._getCtx();
      if (ctx.state !== 'running') { ctx.resume().catch(() => {}); return; }
      if (this._breakBuffer) {
        const src = ctx.createBufferSource();
        src.buffer = this._breakBuffer;
        src.connect(ctx.destination);
        src.start(0);
        return;
      }
      // Fallback: absteigender Einzelton
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(440, now);
      osc.frequency.exponentialRampToValueAtTime(220, now + 0.4);
      gain.gain.setValueAtTime(0.4, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
      osc.start(now);
      osc.stop(now + 0.55);
    } catch (e) {}
  }

  _preloadBellStart() {
    if (this._preloadBellStartAttempted) return;
    this._preloadBellStartAttempted = true;
    try {
      const ctx = this._getCtx();
      loadArrayBuffer(soundUrl('bellstart'))
        .then((buf) => ctx.decodeAudioData(buf))
        .then((decoded) => { this._bellStartBuffer = decoded; })
        .catch(() => {});
    } catch (e) {}
  }

  /** Spielt bellstart.mp3 zu Beginn jeder Kampfsport-Runde (Gong). */
  playBellStart() {
    try {
      const ctx = this._getCtx();
      if (ctx.state !== 'running') { ctx.resume().catch(() => {}); return; }
      if (this._bellStartBuffer) {
        const src = ctx.createBufferSource();
        src.buffer = this._bellStartBuffer;
        src.connect(ctx.destination);
        src.start(0);
        return;
      }
      // Fallback: zwei kurze Glocken-Schläge (sine bei 1200/900 Hz)
      const now = ctx.currentTime;
      [[1200, 0], [900, 0.15]].forEach(([freq, offset]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now + offset);
        gain.gain.setValueAtTime(0.5, now + offset);
        gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.6);
        osc.start(now + offset);
        osc.stop(now + offset + 0.65);
      });
    } catch (e) {}
  }

  destroy() {
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    document.removeEventListener('touchstart', this._onTouchStart);
    if (this._sessionKeepAlive) {
      this._sessionKeepAlive.pause();
      this._sessionKeepAlive.src = '';
      this._sessionKeepAlive = null;
    }
    if (this._keepAliveNode) {
      try { this._keepAliveNode.stop(); } catch (e) {}
      this._keepAliveNode = null;
    }
    if (this._ctx) {
      this._ctx.close().catch(() => {});
      this._ctx = null;
    }
  }
}
